/**
 * Inline SVG icons for the app shell. Drawn by hand rather than pulled from an icon
 * package: the stack skill forbids ad-hoc icon packages, and these stay sharp, follow
 * `currentColor` in both colour schemes, and add no network request.
 */
const line = {
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.8,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
} as const;

function Icon({ children }: { children: React.ReactNode }) {
  return (
    <svg viewBox="0 0 24 24" width="24" height="24" aria-hidden="true" focusable="false">
      <g {...line}>{children}</g>
    </svg>
  );
}

export function HomeIcon() {
  return (
    <Icon>
      <path d="M3.5 10.5 12 3.5l8.5 7" />
      <path d="M5.5 9.6V20a.9.9 0 0 0 .9.9h11.2a.9.9 0 0 0 .9-.9V9.6" />
    </Icon>
  );
}

export function PlusIcon() {
  return (
    <Icon>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 8.2v7.6M8.2 12h7.6" />
    </Icon>
  );
}

export function InvestIcon() {
  return (
    <Icon>
      <path d="M3.5 17.5 9 11l3.5 3.5L20.5 6" />
      <path d="M15.5 6h5v5" />
    </Icon>
  );
}

export function LogOutIcon() {
  return (
    <Icon>
      <path d="M10 20.5H6.5a2 2 0 0 1-2-2v-13a2 2 0 0 1 2-2H10" />
      <path d="M15.5 8.5 19.5 12l-4 3.5" />
      <path d="M19.5 12H9.5" />
    </Icon>
  );
}

export function MenuIcon() {
  return (
    <Icon>
      <path d="M4 7h16M4 12h16M4 17h16" />
    </Icon>
  );
}

export function ProfileIcon() {
  return (
    <Icon>
      <circle cx="12" cy="8.5" r="3.6" />
      <path d="M5 20c0-3.3 3.1-5.4 7-5.4s7 2.1 7 5.4" />
    </Icon>
  );
}

export function WalletIcon() {
  return (
    <Icon>
      <path d="M3.5 8.5a2 2 0 0 1 2-2h13a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-13a2 2 0 0 1-2-2z" />
      <path d="M3.5 10.5h17" />
      <circle cx="16.5" cy="14.5" r="1.2" />
    </Icon>
  );
}

export function HouseholdIcon() {
  return (
    <Icon>
      <circle cx="9" cy="8.5" r="3.2" />
      <path d="M3 19.5c0-2.9 2.7-4.7 6-4.7s6 1.8 6 4.7" />
      <path d="M16 6.2a3.2 3.2 0 0 1 0 6M17.5 15.2c2 .6 3.5 2.1 3.5 4.3" />
    </Icon>
  );
}

export function IncomeIcon() {
  return (
    <Icon>
      <path d="M12 20V5" />
      <path d="M6.5 10.5 12 5l5.5 5.5" />
      <path d="M4 20h16" />
    </Icon>
  );
}

/** A ticked list: the month's outstanding jobs, and the button that closes it. */
export function ChecklistIcon() {
  return (
    <Icon>
      <path d="M4 6.5 5.5 8 8 5.5M4 12.5 5.5 14 8 11.5M4 18.5 5.5 20 8 17.5" />
      <path d="M11 7h9M11 13h9M11 19h9" />
    </Icon>
  );
}

/** A phone screen with a tick: the wallet's own numbers, squared against the books. */
export function ReconcileIcon() {
  return (
    <Icon>
      <rect x="6" y="3" width="12" height="18" rx="2" />
      <path d="M9.5 12.5 11 14l3.5-3.5" />
    </Icon>
  );
}

/** Two arrows chasing each other: something that comes back every month. */
export function RepeatIcon() {
  return (
    <Icon>
      <path d="M4 9a5 5 0 0 1 5-5h7l-2.5-2.5M20 15a5 5 0 0 1-5 5H8l2.5 2.5" />
    </Icon>
  );
}

export function TagIcon() {
  return (
    <Icon>
      <path d="M4 12.5V5.5a1.5 1.5 0 0 1 1.5-1.5h7l7.5 7.5-8.5 8.5z" />
      <circle cx="8.5" cy="8.5" r="1.3" />
    </Icon>
  );
}
